import React, { useEffect, useState } from 'react';
import {
    Box,
    TextField,
    Typography,
    Paper,
    Grid,
    Chip,
    Stack,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';
import filterSectionStyle from '../../styles/filterSectionStyle';
import apiClient from '../../axiosClient';
import { INTERACTION_TYPES } from '../../constants/interactionTypes';

const AgentStatsBoard = () => {
    const today = new Date().toISOString().split('T')[0];
    const [agencyReport, setAgencyReport] = useState<any>({});
    const [agentsReport, setAgentsReport] = useState<Record<string, any> | null>(null);
    const [selectedDate, setSelectedDate] = useState<string>(today);

    const extraKeys = ['Call', 'Visit', 'Followup Date Reported', 'PTP Date Reported'];

    const getAgencyReport = async () => {
        try {
            const response = await apiClient.get(
                `/api/v1/interaction/agency_today_report?operation_date=${encodeURIComponent(selectedDate)}`
            );
            setAgencyReport(response.data.agency_report ?? {});
            setAgentsReport(response.data.agents_report ?? {});
        } catch (error) {
            alert(`Error fetching agency report: ${error}`);
            console.error('Error fetching agency report:', error);
        }
    };

    useEffect(() => {
        getAgencyReport();
    }, [selectedDate]);

    // Combined ordered keys for agent chips: INTERACTION_TYPES first, then extraKeys.
    const agentChipKeys = [...INTERACTION_TYPES, ...extraKeys];

    return (
        <Box m={2}>
            <Box sx={filterSectionStyle}>
                <TextField
                    type="date"
                    value={selectedDate}
                    onChange={(event) => setSelectedDate(event.target.value)}
                    fullWidth
                    size="small"
                />
            </Box>

            {/* Agency Interactions Report for selected Date */}
            <Box sx={{ width: '100%', marginTop: '4px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Interactions Report</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Total interactions: <strong>{agencyReport?.total_interactions ?? 0}</strong>
                    </Typography>
                </Box>

                {/* top extra keys row */}
                {extraKeys.length > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 2 }}>
                        <Grid container spacing={2} justifyContent="center">
                            {extraKeys.map((key) => (
                                <Grid key={key}>
                                    <Paper
                                        elevation={1}
                                        sx={{
                                            // p: 1,
                                            borderRadius: 2,
                                            minHeight: 16,
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap="4px"
                                            sx={{
                                                border: "1px solid",
                                                borderColor: "info.dark", // theme light-blue; change to a hex if you prefer
                                                borderRadius: 1,
                                                px: 1, // horizontal padding
                                                py: 0.5, // vertical padding
                                            }}
                                        >
                                            <Typography variant="caption" color="text.secondary">{key} :</Typography>
                                            <Typography variant="body2">{(agencyReport as any)[key] ?? 0}</Typography>
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}

                {/* main interaction types row */}
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 2 }}>
                    <Grid container spacing={2}>
                        {INTERACTION_TYPES.map((type) => (
                            <Grid key={type} >
                                <Paper
                                    elevation={1}
                                    sx={{
                                        p: 1,
                                        borderRadius: 2,
                                        minHeight: 16,
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                >
                                    <Box display='flex' alignItems='center' gap='4px'>
                                        <Typography variant="caption" color="text.secondary">{type} :</Typography>
                                        <Typography variant='body2'>{(agencyReport as any)[type] ?? 0}</Typography>
                                    </Box>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Box>

            {/* ---------------------------
              AGENTS REPORT (compact list, ordered chips)
             --------------------------- */}
            <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6">Agents Report</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Agents: <strong>{agentsReport ? Object.keys(agentsReport).length : 0}</strong>
                    </Typography>
                </Box>

                <Paper elevation={1} sx={{ p: 0.5, borderRadius: 1 }}>
                    {agentsReport && Object.entries(agentsReport).length > 0 ? (
                        <List dense disablePadding>
                            {Object.entries(agentsReport).map(([username, stats]) => {
                                const total = stats?.total_interactions ?? 0;

                                return (
                                    <ListItem
                                        key={username}
                                        sx={{
                                            py: 0.5,
                                            px: 1,
                                            borderBottom: '1px solid',
                                            borderColor: 'divider',
                                            alignItems: 'center',
                                        }}
                                        secondaryAction={
                                            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                                                <strong>{total}</strong>
                                            </Typography>
                                        }
                                    >
                                        <ListItemText
                                            primary={
                                                <Typography variant="subtitle2" sx={{ textTransform: 'capitalize', fontSize: 13 }}>
                                                    {username}
                                                </Typography>
                                            }
                                            secondary={
                                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                                                    {agentChipKeys.map((k) => {
                                                        const value = stats?.[k] ?? 0;
                                                        return (
                                                            <Chip
                                                                key={k}
                                                                label={`${k}: ${value}`}
                                                                size="small"
                                                                variant={value ? 'filled' : 'outlined'}
                                                                sx={{
                                                                    height: 22,
                                                                    fontSize: 11,
                                                                    textTransform: 'capitalize',
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </Stack>
                                            }
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>
                    ) : (
                        <Box p={1}>
                            <Typography variant="body2" color="text.secondary">
                                No agents data for selected date.
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </Box>
        </Box>
    );
};

export default AgentStatsBoard;
